"use client";

import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getInquiryByClientId,
  fetchQuestions,
  updateInquiry,
  addInquiryActivity,
} from "../../../services/AdminService";
import { useAuth } from "../../../context/AuthProvider";
import { mapInquiryListToUi } from "../utils/inquiryMapper";
import { FiArrowRight, FiCalendar, FiClock, FiMail, FiPhone, FiSearch, FiUser } from "react-icons/fi";
import ActivityTimeline from "./ActivityTimeline";

const INQUIRY_REFRESH_KEY = "inquiries_data_changed";
const FILTERS = ["ALL", "OPN", "INP", "CRS", "CNR", "INV", "NIV"];

const text = (v, f = "-") => (v === undefined || v === null || v === "" ? f : v);
const formatId = (id) => `CBR-${String(id || "").replace(/\D/g, "").padStart(3, "0") || "000"}`;
const maskEmail = (email) => {
  if (!email || email === "-") return "-";
  const [user, domain] = email.split("@");
  if (!user || !domain) return "****@****";
  const maskedUser = user.length <= 4 ? `${user[0]}***` : `${user.slice(0, 2)}****${user.slice(-2)}`;
  const parts = domain.split(".");
  return `${maskedUser}@${parts[0].slice(0, 2)}****.${parts.slice(1).join(".")}`;
};
const maskContact = (num) => {
  if (!num || num === "-") return "-";
  const d = String(num).replace(/\D/g, "");
  return d.length < 4 ? "***" : `${"*".repeat(d.length - 3)}${d.slice(-3)}`;
};
const today = () => new Date().toISOString().split("T")[0];
const BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://localhost:3000").replace(/\/+$/, "");
const getHeaders = () => {
  const token = sessionStorage.getItem("token") || localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
    Accept: "application/json",
  };
};
const STATUS_MAP = {
  OPN: { label: "Open", color: "primary" },
  INP: { label: "In Progress", color: "warning" },
  CRS: { label: "Resolved", color: "success" },
  CNR: { label: "No Response", color: "danger" },
  INV: { label: "Invoiced", color: "info" },
  NIV: { label: "Not Invoiced", color: "warning" },
};

const STATUS_OPTIONS = [
  { value: "OPN", label: "Open" },
  { value: "INP", label: "In Progress" },
  { value: "CRS", label: "Resolved" },
  { value: "CNR", label: "No Response" },
  { value: "INV", label: "Invoiced" },
  { value: "NIV", label: "Not Invoiced" },
];

const getStatusMeta = (statusCode) => {
  const normalized = String(statusCode || "").toUpperCase();
  return STATUS_MAP[normalized] || {
    label: normalized || "No Status",
    color: "secondary",
  };
};

const getStatusLabel = (statusCode) => getStatusMeta(statusCode).label;

const stripLegacyUserTags = (value = "") => String(value || "").replace(/\[user_id:\d+\]/gi, "").trim();

export default function LeadStatus({ isAgent = false }) {
  const { user, authReady } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const inquiryIdFromUrl = searchParams.get("inquiryId");

  const [clientId, setClientId] = useState("");
  const [allInquiries, setAllInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [queueSearch, setQueueSearch] = useState("");
  const [queueFilter, setQueueFilter] = useState("ALL");
  const [botQuestions, setBotQuestions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [showLegacy, setShowLegacy] = useState(false);
  const [formData, setFormData] = useState({ status: "", agent_remarks: "", Next_followup: "" });

  const getId = (r) => r?.id ?? r?.ticket_id ?? r?.ticketId ?? "-";
  const getStatus = (r) => r?.status_code ?? r?.status ?? "";
  const getName = (r) => r?.client ?? "-";
  const getContact = (r) => r?.contact ?? "-";
  const getEmail = (r) => r?.email ?? "-";
  const getLastQuestionMeta = (record) => {
    const raw = record?.last_question ?? record?.lastQuestion ?? null;

    if (raw && typeof raw === "object") {
      const inferredText = raw.text ?? raw.question_text ?? raw.question ?? "";
      const inferredId = raw.id ?? raw.question_id ?? null;
      const inferredLevel = raw.level ?? raw.question_level ?? null;
      return {
        id: inferredId != null ? String(inferredId).trim() : "",
        level: inferredLevel != null ? String(inferredLevel).trim() : "",
        text: String(inferredText || "").trim(),
      };
    }

    const fallbackText = record?.last_question_text ?? raw ?? "";
    const rawString = raw != null ? String(raw).trim() : "";
    const looksNumeric = /^\d+$/.test(rawString);

    return {
      id: "",
      level: looksNumeric ? rawString : "",
      text: String(fallbackText || "").trim(),
    };
  };

  const getQuestion = (r) => r?.inquiry_type ?? (getLastQuestionMeta(r).text || "-");
  const getOther = (r) => r?.details_full ?? r?.details ?? "-";
  const getFollowup = (r) => r?.next_followup_value ?? r?.next_followup ?? "";
  const getCreated = (r) => r?.created_at ?? "-";
  const getRemarks = (r) => r?.remarks_full ?? r?.remarks ?? "-";
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");

  const broadcast = useCallback(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(INQUIRY_REFRESH_KEY, Date.now().toString());
  }, []);

  const loadInquiries = useCallback(async (cid) => {
    if (!cid) return;
    setLoading(true);
    setError("");
    try {
      const response = await getInquiryByClientId(cid, 1, 1000);
      console.log("API RAW:", response);
      let inquiries = response?.status && Array.isArray(response?.data)
        ? mapInquiryListToUi(response.data, {
            detailsMaxLength: 160,
            remarksMaxLength: 160,
          })
        : [];
      console.log("MAPPED:", inquiries);
      if (isAgent && user?.userId) {
        inquiries = inquiries.filter((inq) => parseInt(inq.assigned_to || 0, 10) === parseInt(user.userId, 10));
      }
      setAllInquiries(inquiries);
    } catch (e) {
      setError(e?.message || "Failed to load inquiries");
      setAllInquiries([]);
      setSelectedInquiry(null);
    } finally {
      setLoading(false);
    }
  }, [isAgent, user?.userId]);

  const extractQuestionsFromResponse = (response) => {
    const collected = [];
    const seenIds = new Set();

    const pushIfQuestion = (item) => {
      if (!item || typeof item !== "object") return;
      const hasId = Object.prototype.hasOwnProperty.call(item, "id");
      const hasQuestionText = Object.prototype.hasOwnProperty.call(item, "question_text");
      const hasQuestionLevel = Object.prototype.hasOwnProperty.call(item, "question_level");
      if (!hasId || (!hasQuestionText && !hasQuestionLevel)) return;
      if (seenIds.has(item.id)) return;
      seenIds.add(item.id);
      collected.push(item);
    };

    const walk = (val) => {
      if (!val) return;
      if (Array.isArray(val)) {
        val.forEach((node) => {
          if (node && typeof node === "object") {
            pushIfQuestion(node);
            Object.values(node).forEach((x) => walk(x));
          }
        });
        return;
      }
      if (typeof val === "object") {
        pushIfQuestion(val);
        Object.values(val).forEach((x) => walk(x));
      }
    };

    walk(response);
    return collected;
  };

  const loadBotQuestions = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const response = await fetchQuestions(cid);
      const list = extractQuestionsFromResponse(response).sort(
        (a, b) => (a.question_level || 0) - (b.question_level || 0)
      );
      setBotQuestions(list);
    } catch (e) {
      console.error("Unable to load bot questions for hierarchy:", e);
      setBotQuestions([]);
    }
  }, []);

  const fetchActivities = useCallback(async (id) => {
    if (!id) {
      setActivities([]);
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}/inquiry/activity/${id}`, {
        headers: getHeaders(),
      });
      const data = await res.json();
      setActivities(data.data || []);
    } catch (err) {
      console.error("Error fetching activities:", err);
      setActivities([]);
    }
  }, []);

  useEffect(() => {
    if (!authReady) return;
    const cid = sessionStorage.getItem("client_id") || sessionStorage.getItem("clientId") || "";
    if (!cid) return;
    setClientId(cid);
    loadBotQuestions(cid);
  }, [authReady, loadBotQuestions]);

  useEffect(() => {
    if (!clientId) return;
    loadInquiries(clientId);
  }, [clientId, loadInquiries]);

  useEffect(() => {
    if (loading) return;

    if (!allInquiries.length) {
      setSelectedInquiry(null);
      return;
    }

    const selectedByUrl = inquiryIdFromUrl
      ? allInquiries.find((inquiry) => String(getId(inquiry)) === String(inquiryIdFromUrl))
      : null;

    if (inquiryIdFromUrl && !selectedByUrl) {
      setSelectedInquiry(null);
      setError(`Inquiry ${inquiryIdFromUrl} not found.`);
      return;
    }

    setSelectedInquiry(selectedByUrl || allInquiries[0] || null);
    setShowLegacy(false);
    setError("");
  }, [allInquiries, inquiryIdFromUrl, loading]);

  useEffect(() => {
    if (!selectedInquiry) return;
    setFormData({
      status: getStatus(selectedInquiry),
      agent_remarks: "",
      Next_followup: getFollowup(selectedInquiry),
    });
    setOk("");
  }, [selectedInquiry]);

  useEffect(() => {
    if (selectedInquiry) {
      fetchActivities(getId(selectedInquiry));
    } else {
      setActivities([]);
    }
  }, [selectedInquiry, fetchActivities]);

  useEffect(() => {
    if ((formData.status === "CRS" || formData.status === "CNR") && formData.Next_followup) {
      setFormData((prev) => ({
        ...prev,
        Next_followup: "",
      }));
    }
  }, [formData.status, formData.Next_followup]);

  const normalizedSearch = queueSearch.trim().toLowerCase();
  const visibleInquiries = allInquiries.filter((i) => {
    const s = String(getStatus(i) || "").toUpperCase();
    if (queueFilter !== "ALL" && s !== queueFilter) return false;
    if (!normalizedSearch) return true;
    const blob = [formatId(getId(i)), getName(i), getContact(i), getEmail(i), s].join(" ").toLowerCase();
    return blob.includes(normalizedSearch);
  });

  const resolveInquiryHierarchy = (inquiry) => {
    if (!inquiry || !botQuestions.length) return [];

    const lastQuestion = getLastQuestionMeta(inquiry);
    const lastQuestionText = String(lastQuestion.text || "").toLowerCase();

    let target =
      botQuestions.find((q) => String(q.id || "").trim() === lastQuestion.id) ||
      botQuestions.find(
        (q) =>
          lastQuestion.level &&
          String(q.question_level || "").trim() === String(lastQuestion.level)
      ) ||
      botQuestions.find(
        (q) => String(q.question_text || "").trim().toLowerCase() === lastQuestionText
      );

    if (!target) return [];

    const path = [target.question_text || `Level ${target.question_level}`];
    let current = target;
    let safety = 0;

    while (
      current &&
      current.question_parent_level !== null &&
      current.question_parent_level !== undefined &&
      Number(current.question_parent_level) !== 0 &&
      safety < 50
    ) {
      const parentLevel = Number(current.question_parent_level);
      const parent = botQuestions.find(
        (q) => Number(q.question_level) === parentLevel
      );
      if (!parent) break;
      path.unshift(parent.question_text || `Level ${parent.question_level}`);
      current = parent;
      safety += 1;
    }

    return path;
  };

  const inquiryHierarchyPath = resolveInquiryHierarchy(selectedInquiry);
  const additionalDetailsText = text(getOther(selectedInquiry), "-");
  const agentRemarksText = text(getRemarks(selectedInquiry), "-");
  const legacyNotes = stripLegacyUserTags(selectedInquiry?.remarks_full || "");
  const hasLegacyNotes = legacyNotes && legacyNotes.replace(/[\s,]+/g, "") !== "";
  const legacyNoteLines = legacyNotes
    ? legacyNotes.split(/\r?\n+/).map((line) => line.trim()).filter(Boolean)
    : [];
  const filteredActivities = activities;

  const onSelect = (inq) => {
    setSelectedInquiry(inq);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("inquiryId", getId(inq));
    setSearchParams(nextParams, { replace: true });
    setError("");
    setOk("");
  };

  const onUpdate = async () => {
    if (!selectedInquiry || !formData.status) return setError("Please select a status");
    if ((formData.status === "INV" || formData.status === "NIV") && getStatus(selectedInquiry) !== "CRS") {
      return setError("Ticket must be resolved before marking as Invoiced.");
    }

    if (isSubmitting) return;

    const currentStatus = String(getStatus(selectedInquiry) ?? "").toUpperCase();
    const currentFollowup = String(getFollowup(selectedInquiry) ?? "");
    const nextFollowup = String(formData.Next_followup || "");
    const nextRemarks = String(formData.agent_remarks || "").trim();
    const nextStatus = String(formData.status || "").toUpperCase();

    const isStatusChanged = nextStatus !== currentStatus;
    const isRemarkAdded = nextRemarks !== "";
    const isFollowupChanged = nextFollowup !== currentFollowup;

    if (currentFollowup && nextFollowup === currentFollowup && !isRemarkAdded && !isStatusChanged) {
      alert("Follow-up already set to same date");
      return;
    }

    if (
      nextStatus === currentStatus &&
      !nextRemarks &&
      nextFollowup === currentFollowup
    ) {
      alert("No changes made");
      return;
    }

    if (!isStatusChanged && !isRemarkAdded && !isFollowupChanged) {
      alert("No changes made");
      return;
    }

    setIsSubmitting(true);
    setSaving(true);
    setError("");
    try {
      const current = getStatus(selectedInquiry);
      const isFinal = current === "CLS" || current === "CNR" || current === "INV" || current === "NIV";
      let remark = nextRemarks || null;

      if (!remark && isFollowupChanged) {
        remark = `Follow-up updated to ${formData.Next_followup}`;
      }

      if (!remark && isStatusChanged) {
        remark = `Status changed to ${getStatusLabel(formData.status)}`;
      }

      const finalRemarks = remark ? stripLegacyUserTags(remark) : "";
      const followup_date = formData.status === "CRS" || formData.status === "CNR"
        ? null
        : formData.Next_followup || null;

      await updateInquiry({
        p_id: getId(selectedInquiry),
        p_idstatus: formData.status,
        p_idagent_remarks: finalRemarks,
        p_idNext_followup: followup_date,
        user_id: user?.userId,
        isFinalStatus: isFinal,
      });

      if (isStatusChanged || isRemarkAdded || isFollowupChanged) {
        const res = await addInquiryActivity({
          inquiry_id: getId(selectedInquiry),
          status: formData.status,
          remark,
          followup_date,
          created_by: user?.userId
        });

        if (!res?.success) {
          console.warn(res?.message);
          alert(res?.message || "No meaningful changes");
          return;
        }

        alert("Updated successfully");
        broadcast();
        fetchActivities(getId(selectedInquiry));
        setTimeout(() => loadInquiries(clientId), 700);
      }
    } catch (e) {
      setError(e?.message || "Failed to update ticket");
    } finally {
      setSaving(false);
      setIsSubmitting(false);
    }
  };

  if (!clientId) return <div className="container-fluid py-4"><div className="alert alert-info">No Client ID detected.</div></div>;

  return (
    <div className="container-fluid py-4">
      <div className="card border-0 shadow-sm lead-status-hero mb-4">
        <div className="card-body p-4">
          <div className="lead-status-kicker">Lead Workspace</div>
          <h3 className="mb-1 fw-bold">{isAgent ? "Assigned Inquiry Details" : "Lead Status Management"}</h3>
          <p className="text-muted mb-0">Track inquiries, update status, and manage follow-ups in one place.</p>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-xl-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-light border-bottom d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fw-bold">Inquiry Queue</h5>
              <span className="badge bg-primary-subtle text-primary">{visibleInquiries.length} of {allInquiries.length}</span>
            </div>
            <div className="p-3 border-bottom">
              <div className="input-group input-group-sm mb-2">
                <span className="input-group-text bg-white border-end-0"><FiSearch size={14} /></span>
                <input className="form-control border-start-0" value={queueSearch} onChange={(e) => setQueueSearch(e.target.value)} placeholder="Search queue" />
              </div>
              <div className="d-flex flex-wrap gap-2">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    className={`btn btn-sm rounded-pill ${queueFilter === f ? "btn-dark" : "btn-outline-secondary"}`}
                    onClick={() => setQueueFilter(f)}
                    title={f === "ALL" ? "All statuses" : getStatusLabel(f)}
                  >
                    {f === "ALL" ? "All" : getStatusLabel(f)}
                  </button>
                ))}
              </div>
            </div>
            <div className="card-body p-0">
              {loading ? <div className="p-3 text-center text-muted">Loading tickets...</div> : (
                <div className="list-group list-group-flush lead-status-queue">
                  {visibleInquiries.length === 0 ? <div className="p-3 text-muted text-center">No tickets found.</div> : visibleInquiries.map((inq) => (
                    <button key={getId(inq)} className={`list-group-item list-group-item-action text-start p-3 border-0 border-bottom lead-list-item ${selectedInquiry && getId(selectedInquiry) === getId(inq) ? "lead-list-item-active" : ""}`} onClick={() => onSelect(inq)}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div><div className="lead-list-id">{formatId(getId(inq))}</div><div className="fw-bold">{getName(inq)}</div></div>
                        <span className="lead-mobile-status">{getStatusLabel(getStatus(inq))}</span>
                      </div>
                      <small className="text-muted d-flex align-items-center gap-2 mt-2"><FiPhone size={13} />{maskContact(getContact(inq))}</small>
                      <small className="text-muted d-flex align-items-center gap-2 mt-1"><FiMail size={13} />{maskEmail(getEmail(inq))}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-8">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light border-bottom"><h5 className="mb-0 fw-bold">{selectedInquiry ? `Update ${formatId(getId(selectedInquiry))} - ${getName(selectedInquiry)}` : "Select a Ticket to Update"}</h5></div>
            <div className="card-body">
              {loading ? <div className="text-center text-muted py-5">Loading inquiry details...</div> : !selectedInquiry ? <div className="text-center text-muted py-5">Select a ticket from queue to start.</div> : (
                <>
                  <div className="lead-status-overview mb-4">
                    <div className="lead-overview-card"><span className="lead-overview-icon"><FiUser size={16} /></span><div><div className="lead-overview-label">Client</div><div className="lead-overview-value">{getName(selectedInquiry)}</div></div></div>
                    <div className="lead-overview-card"><span className="lead-overview-icon"><FiClock size={16} /></span><div><div className="lead-overview-label">Status</div><div className="lead-overview-value">{getStatusLabel(getStatus(selectedInquiry))}</div></div></div>
                    <div className="lead-overview-card"><span className="lead-overview-icon"><FiCalendar size={16} /></span><div><div className="lead-overview-label">Next Follow-up</div><div className="lead-overview-value">{fmtDate(getFollowup(selectedInquiry))}</div></div></div>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label className="form-label small text-muted mb-1">Inquiry Type</label>
                      <div className="fw-bold">{getQuestion(selectedInquiry)}</div>

                      {inquiryHierarchyPath.length > 0 && (
                        <div className="lead-hierarchy-row mt-2">

                          <div className="lead-hierarchy-label text-muted small">
                            Inquiry Journey
                          </div>

                          <div className="lead-hierarchy-breadcrumb fw-medium">
                            {inquiryHierarchyPath.join(" → ")}
                          </div>

                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small text-muted mb-1">Additional Details</label>
                      <div className="fw-bold">{additionalDetailsText}</div>
                    </div>
                    <div className="col-md-6"><label className="form-label small text-muted mb-1">Agent Remarks</label><div className="fw-bold">{agentRemarksText}</div></div>
                    <div className="col-md-6"><label className="form-label small text-muted mb-1">Email</label><div className="fw-bold">{getEmail(selectedInquiry)}</div></div>
                    <div className="col-md-6"><label className="form-label small text-muted mb-1">Contact</label><div className="fw-bold">{getContact(selectedInquiry)}</div></div>
                    <div className="col-md-6"><label className="form-label small text-muted mb-1">Created On</label><div className="fw-bold">{text(getCreated(selectedInquiry), "N/A")}</div></div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Lead Status</label>
                    <select className="form-select" value={formData.status} onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value }))}>
                      <option value="">Select Status</option>
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Next Follow-up Date</label>
                    <input type="date" className="form-control" min={today()} value={formData.Next_followup} onChange={(e) => setFormData((p) => ({ ...p, Next_followup: e.target.value }))} disabled={formData.status === "CRS" || formData.status === "CNR"} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Internal Remarks</label>
                    <textarea rows={4} className="form-control" placeholder="Add internal remark" value={formData.agent_remarks} onChange={(e) => setFormData((p) => ({ ...p, agent_remarks: e.target.value }))} />
                  </div>

                  <div className="mt-3">
                    <h6 className="fw-bold">Activity Timeline</h6>

                    {hasLegacyNotes && (
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="fw-bold mb-0">Previous Notes (Legacy)</h6>
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-decoration-none px-0"
                            onClick={() => setShowLegacy((prev) => !prev)}
                          >
                            {showLegacy ? "Hide Notes" : "Show Notes"}
                          </button>
                        </div>

                        {showLegacy && (
                          <div className="mt-2 p-2 border rounded bg-light small text-muted">
                            {legacyNoteLines.map((line, index) => (
                              <div key={`${index}-${line}`} className="mb-1">
                                • {line}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                      <ActivityTimeline activities={filteredActivities} />
                    </div>
                  </div>

                  {error ? <div className="alert alert-info">{error}</div> : null}
                  {ok ? <div className="alert alert-success">{ok}</div> : null}

                  <div className="d-flex justify-content-end gap-2 lead-status-actions">
                    <button className="btn btn-outline-secondary" onClick={() => setFormData({ status: getStatus(selectedInquiry), agent_remarks: "", Next_followup: getFollowup(selectedInquiry) })}>Clear</button>
                    <button className="btn btn-primary" onClick={onUpdate} disabled={saving || isSubmitting}>
                      {saving ? "Updating..." : <>Update Status <FiArrowRight className="ms-1" size={14} /></>}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .lead-status-hero{background:radial-gradient(circle at top left,rgba(37,99,235,.16),transparent 34%),radial-gradient(circle at bottom right,rgba(14,165,233,.14),transparent 32%),linear-gradient(145deg,#fff 0%,#f8fbff 100%);border-radius:24px!important}
        .lead-status-kicker{font-size:.72rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:#2563eb;margin-bottom:6px}
        .lead-status-queue{max-height:500px;overflow-y:auto}
        .lead-list-item{transition:background-color .2s ease,color .2s ease}
        .lead-list-id{font-size:.75rem;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:#2563eb;margin-bottom:4px}
        .lead-mobile-status{display:inline-flex;align-items:center;padding:6px 10px;border-radius:999px;font-size:.76rem;font-weight:700;background:#f1f5f9;color:#334155}
        .lead-status-overview{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}
        .lead-overview-card{display:flex;align-items:center;gap:12px;padding:14px 16px;border-radius:18px;background:#f8fafc;border:1px solid #e2e8f0}
        .lead-overview-icon{width:40px;height:40px;border-radius:14px;display:inline-flex;align-items:center;justify-content:center;background:#dbeafe;color:#2563eb;flex-shrink:0}
        .lead-overview-label{color:#64748b;font-size:.78rem;font-weight:700;text-transform:uppercase;letter-spacing:.08em}
        .lead-overview-value{color:#0f172a;font-weight:700;margin-top:2px}
        .lead-hierarchy-row{border:1px solid #dbeafe;background:#f8fbff;border-radius:10px;padding:.5rem}
        .lead-hierarchy-label{font-size:.72rem;color:#1d4ed8;font-weight:800;text-transform:uppercase;letter-spacing:.08em;margin-bottom:.35rem}
        .lead-hierarchy-breadcrumb{margin-top:.42rem;font-size:.78rem;font-weight:700;color:#334155;word-break:break-word}
        .lead-list-item-active{background-color:#3e4653!important;color:#fff!important}
        .lead-list-item-active .text-muted{color:rgba(255,255,255,.85)!important}
        .lead-list-item-active:hover{background-color:#292f38!important}
        @media (max-width:767.98px){.lead-status-overview{grid-template-columns:1fr}.lead-status-actions{position:sticky;bottom:8px;padding:12px;background:rgba(255,255,255,.95);border:1px solid #e2e8f0;border-radius:18px;backdrop-filter:blur(12px);z-index:2}.lead-status-actions .btn{flex:1}}
      `}</style>
    </div>
  );
}
